import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomTable from "../../components/common/CustomTable";
import {
  EmptyState,
  EmptyStateTitle,
  EmptyStateDescription,
  ActionButtonsContainer,
  TableActionButton,
} from "../../styles/BundleDeal.styled";
import { ROUTES } from "../../constants/routes";
import { Box } from "@mui/material";
import ResponsivePagination from "../../components/pagination/ResponsivePagination";
import { ALERT_TITLES, defaultPageSize } from "../../constants";
import {
  alertMessage,
} from "../../services/alertService";
import { POST } from "../../services/HTTPServices";
import {
  ActionBar,
  SearchContainer,
  SearchIcon,
  SearchInput,
  SearchWrapper,
  SectionTitle,
  TabContainer,
} from "../../styles/FoodPage.styles";
import searchSvg from "../../assets/svgs/FoodMenu/searchIcon.svg";
import { LanguageText } from "../../styles/components";


const BundleDeal: React.FC = () => {
  const navigate = useNavigate();
  const [bundles, setBundles] = useState<any[]>([]);
  const [filteredBundles, setFilteredBundles] = useState<any[]>([]);
  const [paginatedBundles, setPaginatedBundles] = useState<any[]>([]);
  const [pageNo, setPageNo] = React.useState(1);
  const [totalPageCount, setTotalPageCount] = React.useState(0);
  const [search, setSearch] = React.useState("");

  useEffect(() => {
    const _localAuth: any = JSON.parse(
      localStorage.getItem("authUser") || "{}"
    );
    if (_localAuth) {
      loadBundleDeals(_localAuth.id);
    }
  }, []);

  useEffect(() => {
    if (!search.trim()) {
      setFilteredBundles(bundles);
    } else {
      const filtered = bundles.filter((bundle) => {
        const searchTerm = search.toLowerCase();
        const bundleName = bundle.name?.toLowerCase() || "";
        const bundleItems = bundle.bundleItems || [];
        const itemNames = bundleItems
          .map((item: any) => item.foodMenuName?.toLowerCase() || "")
          .join(" ");

        return (
          bundleName.includes(searchTerm) || itemNames.includes(searchTerm)
        );
      });
      setFilteredBundles(filtered);
      setPageNo(1); // Reset to first page when search changes
    }
  }, [bundles, search]);

  useEffect(() => {
    const startIndex = (pageNo - 1) * defaultPageSize;
    const endIndex = startIndex + defaultPageSize;
    const paginatedData = filteredBundles.slice(startIndex, endIndex);
    
    setPaginatedBundles(paginatedData);
    setTotalPageCount(Math.ceil(filteredBundles.length / defaultPageSize));
  }, [filteredBundles, pageNo]);

  const searchChanged = (e: any) => {
    setSearch(e.target.value);
  };

  const keyPress = async (e: any) => {
    if (e.keyCode === 13) {
      if (!search.trim()) {
        setFilteredBundles(bundles);
      } else {
        const filtered = bundles.filter((bundle) => {
          const searchTerm = search.toLowerCase();
          const bundleName = bundle.name?.toLowerCase() || "";
          const bundleItems = bundle.bundleItems || [];
          const itemNames = bundleItems
            .map((item: any) => item.foodMenuName?.toLowerCase() || "")
            .join(" ");

          return (
            bundleName.includes(searchTerm) || itemNames.includes(searchTerm)
          );
        });
        setFilteredBundles(filtered);
        setPageNo(1); // Reset to first page when search changes
      }
    }
  };

  const handleEditBundle = (bundleId: string) => {
    navigate(ROUTES.ADD_NEW_BUNDLE_DEAL.replace(":id", bundleId));
  };

  const pageClicked = async (page: any) => {
    setPageNo(page);
  };

  const loadBundleDeals = async (id: any) => {
    try {
      const response: any = await POST(
        "fooddelivery/GetBundleDealsByServiceProviderId",
        { serviceProviderId: id + "" },
        "provider"
      );

      if (response && response.status === 200 && response.data) {
        // Handle different possible response structures
        let bundleData = [];
        if (response.data.bundleDealList) {
          bundleData = response.data.bundleDealList;
        } else if (Array.isArray(response.data)) {
          bundleData = response.data;
        } else {
          console.warn("Unexpected API response structure:", response.data);
          bundleData = [];
        }

        // Ensure each bundle has the expected structure and filter out invalid items
        const processedBundles = Array.isArray(bundleData)
          ? bundleData
              .filter((item: any) => {
                // Only include items that look like bundle deals (have bundleItems or at least id and name)
                const isValidBundle =
                  item &&
                  (item.bundleItems ||
                    (item.id && item.name && !item.serviceProviderId));
                if (!isValidBundle) {
                }
                return isValidBundle;
              })
              .map((bundle: any) => ({
                id: bundle.id || "",
                name: bundle.name || "Unnamed Bundle",
                photoPath: bundle.photoPath || "",
                price: typeof bundle.price === "number" ? bundle.price : 0,
                bundleItems: Array.isArray(bundle.bundleItems)
                  ? bundle.bundleItems
                  : [],
              }))
          : [];

        setBundles(processedBundles);
      }
    } catch (error) {

      alertMessage(ALERT_TITLES.ERROR, "Failed to load bundle deal");
    } finally {
    }
  };

  return (
    <TabContainer>

      <ActionBar className="mb-16 mt-16">
        <SectionTitle>
          <LanguageText>Bundle Deal List</LanguageText>
        </SectionTitle>

        <SearchContainer className="mb-16">
          <SearchWrapper>
            <SearchIcon>
              <img src={searchSvg} alt="SearchIcon" />
            </SearchIcon>
            <SearchInput
              id="search"
              type="text"
              placeholder="Search Bundle Deal"
              value={search}
              onChange={searchChanged}
              onKeyDown={keyPress}
            />
          </SearchWrapper>
        </SearchContainer>
      </ActionBar>

      {filteredBundles.length === 0 ? (
        <EmptyState>
          <EmptyStateTitle>No bundle deals yet</EmptyStateTitle>
          <EmptyStateDescription>
            Create your first bundle deal to increase sales
          </EmptyStateDescription>
        </EmptyState>
      ) : (
        <>
          <CustomTable
            columns={[
              {
                key: "name",
                header: "Bundle Name",
                width: '300px',
                render: (_, row) => <div className="crop_text_cart_300">{row.name}</div>,
              },

              {
                key: "bundleItems",
                header: "Items",
                width: "20%",
                render: (_, row) => {
                  const items = row.bundleItems || [];
                  return (
                    <div>
                      {items.length > 0
                        ? items.map((item: any, index: number) => (
                            <div key={index}>
                              {`${item.foodMenuName || "Unknown Item"} x ${
                                item.qty || 0
                              }`}
                            </div>
                          ))
                        : "No items"}
                    </div>
                  );
                },
              },
              {
                key: "itemCount",
                header: "Total Items",
                render: (_, row) => {
                  const items = row.bundleItems || [];
                  const total = items.reduce(
                    (sum: number, item: any) => sum + (item.qty || 0),
                    0
                  );
                  return <div>{total}</div>;
                },
              },
              {
                key: "price",
                header: "Price (SGD)",
                render: (_, row) => `$${row.price.toFixed(2)}`,
              },
              {
                key: "actions",
                header: "",
                width: '100px',
                align: "right",
                render: (_, row) => (
                  <ActionButtonsContainer>
                    <TableActionButton
                      $variant="edit"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditBundle(row.id);
                      }}
                    >
                      Edit
                    </TableActionButton>
                  </ActionButtonsContainer>
                ),
              },
            ]}
            data={paginatedBundles}
            emptyMessage="No bundle deals found."
          />
          <Box mt={4} display="flex" justifyContent="center">
              <ResponsivePagination
                currentPage={pageNo}
                totalPageCount={totalPageCount}
                pageSize={defaultPageSize}
                onPageChange={(page: any) => pageClicked(page)}
              />
            </Box>
        </>
      )}
    </TabContainer>
  );
};

export default BundleDeal;
